if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.setActiveMediaHeight();
        this.updateSliderCounter();
        this.replaceMediaArrowButtons();

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        this.mediaResizeObserver?.disconnect();
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      onMediaArrowClick(event, direction) {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

        const mediaItems = this.getMediaItems();
        if (mediaItems.length < 2) return;

        const activeIndex = Math.max(
          mediaItems.findIndex((item) => item.classList.contains('is-active')),
          0
        );
        const nextIndex = (activeIndex + direction + mediaItems.length) % mediaItems.length;
        this.skipNextMediaScroll = true;
        this.setActiveMedia(mediaItems[nextIndex].dataset.mediaId, false);
      }

      replaceMediaArrowButtons() {
        const previousButton = this.elements.viewer.querySelector('button[name="previous"]');
        const nextButton = this.elements.viewer.querySelector('button[name="next"]');

        if (previousButton) {
          const replacement = previousButton.cloneNode(true);
          replacement.removeAttribute('name');
          replacement.removeAttribute('disabled');
          replacement.removeAttribute('aria-disabled');
          replacement.addEventListener('click', (event) => {
            this.onMediaArrowClick(event, -1);
          });
          previousButton.replaceWith(replacement);
        }

        if (nextButton) {
          const replacement = nextButton.cloneNode(true);
          replacement.removeAttribute('name');
          replacement.removeAttribute('disabled');
          replacement.removeAttribute('aria-disabled');
          replacement.addEventListener('click', (event) => {
            this.onMediaArrowClick(event, 1);
          });
          nextButton.replaceWith(replacement);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const mediaItems = this.getMediaItems();
        const activeMedia = mediaItems.find((item) => item.dataset.mediaId === mediaId) || mediaItems[0];
        if (!activeMedia) {
          return;
        }
        mediaItems.forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail &&
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        if (this.skipNextMediaScroll) {
          this.skipNextMediaScroll = false;
        } else {
          window.setTimeout(() => {
            if (!this.mql.matches || this.elements.thumbnails) {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
            const activeMediaRect = activeMedia.getBoundingClientRect();
            // Don't scroll if the image is already in view
            if (activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          });
        }
        this.playActiveMedia(activeMedia);
        this.setActiveMediaHeight(activeMedia);
        this.updateSliderCounter(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;

        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      getMediaItems() {
        return Array.from(this.elements.viewer.querySelectorAll('[id^="Slide-"][data-media-id]'));
      }

      updateSliderCounter(activeMedia = this.elements.viewer.querySelector('[id^="Slide-"][data-media-id].is-active')) {
        const currentCounter = this.elements.viewer.querySelector('.slider-counter--current');
        const totalCounter = this.elements.viewer.querySelector('.slider-counter--total');
        const mediaItems = this.getMediaItems();
        if (!currentCounter || !totalCounter || !mediaItems.length || !activeMedia) return;

        const activeIndex = mediaItems.findIndex((item) => item.dataset.mediaId === activeMedia.dataset.mediaId);
        currentCounter.textContent = `${Math.max(activeIndex, 0) + 1}`;
        totalCounter.textContent = `${mediaItems.length}`;
      }

      setActiveMediaHeight(activeMedia = this.elements.viewer.querySelector('[id^="Slide-"][data-media-id].is-active')) {
        const mediaList = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
        if (!mediaList || !activeMedia) return;

        const updateHeight = () => {
          const height = activeMedia.offsetHeight || activeMedia.getBoundingClientRect().height;
          if (height > 0) mediaList.style.setProperty('--active-media-height', `${height}px`);
        };

        updateHeight();
        this.observeActiveMedia(activeMedia, updateHeight);
        const image = activeMedia.querySelector('img');
        if (image && !image.complete) image.addEventListener('load', updateHeight, { once: true });
      }

      observeActiveMedia(activeMedia, updateHeight) {
        if (!window.ResizeObserver) return;

        const mediaContainer = activeMedia.querySelector('.product-media-container') || activeMedia;
        if (this.observedMediaElement === mediaContainer) return;

        this.mediaResizeObserver?.disconnect();
        this.observedMediaElement = mediaContainer;
        this.mediaResizeObserver = new ResizeObserver(updateHeight);
        this.mediaResizeObserver.observe(mediaContainer);
      }
    }
  );
}
